// @ts-nocheck
import { DropDownFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import ArrowBackward from "@store/Library/Components/Toolkit/Icons/ArrowBackward";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import Bell from "@store/Library/Components/Toolkit/Icons/Bell/BellFill";
import Bookmark from "@store/Library/Components/Toolkit/Icons/Bookmark/BookmarkFill";
import Calendar from "@store/Library/Components/Toolkit/Icons/Calendar/CalendarFill";
import Car from "@store/Library/Components/Toolkit/Icons/Car/CarFill";
import ChevronLeft from "@store/Library/Components/Toolkit/Icons/ChevronLeft";
import ChevronRight from "@store/Library/Components/Toolkit/Icons/ChevronRight";
import Clock from "@store/Library/Components/Toolkit/Icons/Clock/ClockFill";
import Copy from "@store/Library/Components/Toolkit/Icons/Copy/CopyFill";
import Edit from "@store/Library/Components/Toolkit/Icons/Edit/EditFill";
import Email from "@store/Library/Components/Toolkit/Icons/Email/EmailFill";
import Export from "@store/Library/Components/Toolkit/Icons/Export";
import ExternalLink from "@store/Library/Components/Toolkit/Icons/ExternalLink";
import Eye from "@store/Library/Components/Toolkit/Icons/Eye/EyeFill";
import Globe from "@store/Library/Components/Toolkit/Icons/Globe/GlobeFill";
import Heart from "@store/Library/Components/Toolkit/Icons/Heart/HeartFill";
import Import from "@store/Library/Components/Toolkit/Icons/Import";
import Navigation2 from "@store/Library/Components/Toolkit/Icons/Navigation2/Navigation2Fill";
import Options2 from "@store/Library/Components/Toolkit/Icons/Options2/Options2Fill";
import PersonAdd from "@store/Library/Components/Toolkit/Icons/PersonAdd/PersonAddFill";
import Pin from "@store/Library/Components/Toolkit/Icons/Pin/PinFill";
import PlusCircle from "@store/Library/Components/Toolkit/Icons/PlusCircle/PlusCircleFill";
import Save from "@store/Library/Components/Toolkit/Icons/Save/SaveFill";
import Search from "@store/Library/Components/Toolkit/Icons/Search";
import Settings from "@store/Library/Components/Toolkit/Icons/Settings/SettingsFill";
import ShoppingCart from "@store/Library/Components/Toolkit/Icons/ShoppingCart/ShoppingCartFill";
import Tag from "@store/Library/Components/Toolkit/Icons/Tag/TagLine";
import Trash from "@store/Library/Components/Toolkit/Icons/Trash/TrashFill";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionValue,
} from "@toolkit/Framework/Common/Utilities/dataOptions";
import { LiteralUnion } from "@toolkit/Framework/Types/Common";
import { useMemo } from "react";

export type ButtonIconOption = DataOption<IconPresentationProps["src"]>;
export interface ButtonIconOptionMapper {
    none: ButtonIconOption;
    arrowForward: ButtonIconOption;
    arrowBackward: ButtonIconOption;
    chevronRight: ButtonIconOption;
    chevronLeft: ButtonIconOption;
}
export type ButtonIconOptions = DataOptions<ButtonIconOption> & ButtonIconOptionMapper;
export type ButtonIcon = LiteralUnion<keyof ButtonIconOptionMapper>;

export const buttonIconOptions: ButtonIconOptions = {
    none: { id: "none", displayName: "None", value: "", isDefault: true },
    arrowForward: { id: "arrowForward", displayName: "Arrow Forward", value: ArrowForward },
    arrowBackward: { id: "arrowBackward", displayName: "Arrow Backward", value: ArrowBackward },
    bell: { id: "bell", displayName: "Bell", value: Bell },
    bookmark: { id: "bookmark", displayName: "Bookmark", value: Bookmark },
    calendar: { id: "calendar", displayName: "Calendar", value: Calendar },
    cart: { id: "cart", displayName: "Cart", value: ShoppingCart },
    chevronRight: { id: "chevronRight", displayName: "Chevron Right", value: ChevronRight },
    chevronLeft: { id: "chevronLeft", displayName: "Chevron Left", value: ChevronLeft },
    clock: { id: "clock", displayName: "Clock", value: Clock },
    cog: { id: "cog", displayName: "Cog", value: Settings },
    copy: { id: "copy", displayName: "Copy", value: Copy },
    email: { id: "email", displayName: "Email", value: Email },
    export: { id: "export", displayName: "Export", value: Export },
    external: { id: "external", displayName: "External", value: ExternalLink },
    eye: { id: "eye", displayName: "Eye", value: Eye },
    globe: { id: "globe", displayName: "Globe", value: Globe },
    heart: { id: "heart", displayName: "Heart", value: Heart },
    import: { id: "import", displayName: "Import", value: Import },
    navigation: { id: "navigation", displayName: "Navigation", value: Navigation2 },
    options: { id: "options", displayName: "Options", value: Options2 },
    pencil: { id: "pencil", displayName: "Pencil", value: Edit },
    pin: { id: "pin", displayName: "Pin", value: Pin },
    plusCircle: { id: "plusCircle", displayName: "Plus (Circled)", value: PlusCircle },
    save: { id: "save", displayName: "Save", value: Save },
    search: { id: "search", displayName: "Search", value: Search },
    shipping: { id: "shipping", displayName: "Shipping", value: Car },
    tag: { id: "tag", displayName: "Tag", value: Tag },
    trash: { id: "trash", displayName: "Trash Bin", value: Trash },
    userPlus: { id: "userPlus", displayName: "User Plus", value: PersonAdd },
};

export type ButtonIconFieldValue = ButtonIcon | undefined;
export interface ButtonIconFieldOptions {
    isChildDefinition?: boolean;
}

export const getButtonIconFieldData = (value: ButtonIconFieldValue, options?: ButtonIconFieldOptions) => {
    if (!value) {
        return undefined;
    }

    return getDataOptionValue(buttonIconOptions, value);
};

export const getButtonIconFieldDefinition = (name: string, options?: ButtonIconFieldOptions) => {
    const { isChildDefinition } = options || {};

    const definition: DropDownFieldDefinition<string> = {
        ...getDataOptionsFieldDefinition(buttonIconOptions),
        name,
        displayName: "Button Icon",
        fieldType: !isChildDefinition ? "General" : (undefined as any),
        hideEmptyOption: true,
    };

    return definition;
};

const getButtonIconField = (name: string, options?: ButtonIconFieldOptions) => {
    const parse = (value: ButtonIconFieldValue) => getButtonIconFieldData(value, options);

    return {
        definition: getButtonIconFieldDefinition(name, options),
        parse,
        useParse: (...parameters: Parameters<typeof parse>) => {
            return useMemo(() => parse(...parameters), parameters);
        },
    };
};

export default getButtonIconField;
